import { useEffect } from 'react';

import { useLocation } from 'react-router-dom';

import { getLocaleFromPathname, getPathWithoutLocalePrefix, toLocalizedPath } from '@/i18n/locale';
import {
  catalogCategorySlugs,
  isCatalogDetailPath,
  routePaths,
  type CatalogCategorySlug,
} from '@/routes/routeRegistry';

const SITE_URL = 'https://kookje2001.com';
const SITE_NAME = '국제티엔씨';
const DEFAULT_TITLE = '국제티엔씨 | 냉동·냉장 시스템 전문 기업';
const DEFAULT_DESCRIPTION =
  '국제티엔씨는 냉동·냉장 시스템, 공조 기계설비, 에너지 솔루션의 설계·시공·유지관리를 제공하는 토털 엔지니어링 전문 기업입니다.';
const DEFAULT_IMAGE = `${SITE_URL}/og-image.png`;

interface SeoEntry {
  description: string;
  title: string;
}

const catalogCategorySeo: Partial<Record<CatalogCategorySlug, SeoEntry>> = {
  'energy-solution': {
    description: '에너지 절감과 장비 효율화를 함께 설계하는 국제티엔씨 에너지 솔루션을 확인하세요.',
    title: '에너지 솔루션 | 국제티엔씨',
  },
  'mechanical-hvac': {
    description:
      '공조, 냉난방, 위생, 자동제어를 통합 설계·시공·운영하는 국제티엔씨 공조 기계설비 솔루션입니다.',
    title: '공조 기계설비 | 국제티엔씨',
  },
  'refrigeration-system': {
    description:
      '유통, 저온창고, 제빙, 유지보수를 하나로 연결하는 국제티엔씨 냉동·냉장 시스템입니다.',
    title: '냉동·냉장 시스템 | 국제티엔씨',
  },
};

const staticSeoEntries: Partial<Record<string, SeoEntry>> = {
  [routePaths.home]: {
    description: DEFAULT_DESCRIPTION,
    title: DEFAULT_TITLE,
  },
  [routePaths.aboutGreeting]: {
    description:
      '2001년부터 축적한 냉동·냉장 시스템 경험과 기술 경쟁력을 바탕으로 성장해온 국제티엔씨의 인사말입니다.',
    title: 'CEO 인사말 | 국제티엔씨',
  },
  [routePaths.aboutHistory]: {
    description:
      '국제티엔씨가 냉동·냉장 시스템 전문 기업으로 성장해온 주요 연혁과 기술 기반을 확인하세요.',
    title: '회사 연혁 | 국제티엔씨',
  },
  [routePaths.aboutCertification]: {
    description:
      '국제티엔씨의 특허, 인증, 기술 역량 자료를 통해 검증된 냉동·공조 엔지니어링 역량을 확인하세요.',
    title: '특허·인증 | 국제티엔씨',
  },
  [routePaths.aboutOrganization]: {
    description: '국제티엔씨의 조직 구성과 사업 운영 체계를 확인하세요.',
    title: '조직도 | 국제티엔씨',
  },
  [routePaths.aboutLocation]: {
    description: '국제티엔씨 본사 위치, 교통 안내, 대표 연락처를 확인하고 사업 상담을 준비하세요.',
    title: '찾아오시는 길 | 국제티엔씨',
  },
  [routePaths.constructionCases]: {
    description:
      '국제티엔씨가 수행한 냉동·냉장, 공조, 기계설비 시공 사례와 프로젝트 경험을 확인하세요.',
    title: '시공 사례 | 국제티엔씨',
  },
  [routePaths.mediaCoverages]: {
    description: '국제티엔씨의 기술력과 프로젝트 성과를 소개한 언론보도 자료를 확인하세요.',
    title: '언론보도 | 국제티엔씨',
  },
  [routePaths.projectRecords]: {
    description: '국제티엔씨가 축적해온 주요 공사실적과 수행 이력을 확인하세요.',
    title: '공사실적 | 국제티엔씨',
  },
  [routePaths.supportMaterials]: {
    description:
      '국제티엔씨의 기술자료, 교육자료, 제품 정보를 한곳에서 확인할 수 있는 통합 자료실입니다.',
    title: '기술자료 | 국제티엔씨',
  },
  [routePaths.webCatalogs]: {
    description: '국제티엔씨 제품과 솔루션 정보를 웹 카탈로그로 빠르게 확인하세요.',
    title: '웹 카탈로그 | 국제티엔씨',
  },
  [routePaths.promotionalVideos]: {
    description: '국제티엔씨의 브랜드, 기술력, 프로젝트 가치를 담은 홍보영상 아카이브입니다.',
    title: '홍보영상 | 국제티엔씨',
  },
};

const setMetaContent = (selector: string, content: string) => {
  document.head.querySelector<HTMLMetaElement>(selector)?.setAttribute('content', content);
};

const setCanonicalHref = (href: string) => {
  document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]')?.setAttribute('href', href);
};

const setAlternateHref = (hreflang: string, href: string) => {
  let linkElement = document.head.querySelector<HTMLLinkElement>(
    `link[rel="alternate"][hreflang="${hreflang}"]`,
  );

  if (!linkElement) {
    linkElement = document.createElement('link');
    linkElement.rel = 'alternate';
    linkElement.hreflang = hreflang;
    document.head.appendChild(linkElement);
  }

  linkElement.href = href;
};

const isKnownCatalogCategorySlug = (value: string): value is CatalogCategorySlug => {
  return catalogCategorySlugs.some((slug) => slug === value);
};

const resolveSeoEntry = (pathname: string): SeoEntry => {
  const staticEntry = staticSeoEntries[pathname];

  if (staticEntry) return staticEntry;

  const catalogCategorySlug = pathname.replace(/^\//, '');
  const catalogCategoryEntry = isKnownCatalogCategorySlug(catalogCategorySlug)
    ? catalogCategorySeo[catalogCategorySlug]
    : undefined;

  if (catalogCategoryEntry) return catalogCategoryEntry;

  if (isCatalogDetailPath(pathname)) {
    return {
      description: '국제티엔씨의 제품별 상세 사양, 적용 환경, 설계·시공 포인트를 확인하세요.',
      title: '제품 상세 | 국제티엔씨',
    };
  }

  if (pathname.startsWith(`${routePaths.constructionCases}/`)) {
    return {
      description: '국제티엔씨의 현장별 시공 사례와 프로젝트 수행 내용을 확인하세요.',
      title: '시공 사례 상세 | 국제티엔씨',
    };
  }

  if (pathname.startsWith(`${routePaths.supportMaterials}/`)) {
    return {
      description: '국제티엔씨 기술자료와 교육자료의 상세 내용을 확인하세요.',
      title: '기술자료 상세 | 국제티엔씨',
    };
  }

  if (pathname.startsWith(`${routePaths.webCatalogs}/`)) {
    return {
      description: '국제티엔씨 웹 카탈로그 상세 자료를 확인하세요.',
      title: '웹 카탈로그 상세 | 국제티엔씨',
    };
  }

  if (pathname.startsWith(`${routePaths.promotionalVideos}/`)) {
    return {
      description: '국제티엔씨 홍보영상 상세 콘텐츠를 확인하세요.',
      title: '홍보영상 상세 | 국제티엔씨',
    };
  }

  return {
    description: DEFAULT_DESCRIPTION,
    title: DEFAULT_TITLE,
  };
};

const SeoManager = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const locale = getLocaleFromPathname(pathname) ?? 'ko';
    const unprefixedPathname = getPathWithoutLocalePrefix(pathname);
    const { description, title } = resolveSeoEntry(unprefixedPathname);
    const url = new URL(toLocalizedPath(unprefixedPathname, locale), SITE_URL).toString();

    document.title = title;
    setCanonicalHref(url);
    setAlternateHref('ko', new URL(toLocalizedPath(unprefixedPathname, 'ko'), SITE_URL).toString());
    setAlternateHref('en', new URL(toLocalizedPath(unprefixedPathname, 'en'), SITE_URL).toString());
    setAlternateHref('x-default', new URL(unprefixedPathname, SITE_URL).toString());
    setMetaContent('meta[name="description"]', description);
    setMetaContent('meta[property="og:title"]', title);
    setMetaContent('meta[property="og:description"]', description);
    setMetaContent('meta[property="og:url"]', url);
    setMetaContent('meta[property="og:image"]', DEFAULT_IMAGE);
    setMetaContent('meta[property="og:image:secure_url"]', DEFAULT_IMAGE);
    setMetaContent('meta[name="twitter:title"]', title);
    setMetaContent('meta[name="twitter:description"]', description);
    setMetaContent('meta[name="twitter:image"]', DEFAULT_IMAGE);
    setMetaContent('meta[property="og:site_name"]', SITE_NAME);
  }, [pathname]);

  return null;
};

export default SeoManager;
